import {
  Suspense,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { AppFrame, AppFrameProps } from 'src/shared/AppFrame/AppFrame';
import { OnboardingProgressPill } from 'src/shared/AppFrame/OnboardingProgressPill';
import { WelcomeModal } from 'src/shared/AppFrame/WelcomeModal';
import { useIsAdminPortal } from 'src/shared/dreamcoat';

export type { AppFrameProps };

export const initialState: AppFrameProps = {
  title: '',
  collapsedTitle: undefined,
  actions: [],
  row: '',
  frameOnly: false,
  drawerOpen: false,
  compactTitle: false,
  containerMaxWidth: 'lg',
  bgcolor: '',
  children: '',
  disabled: false,
};

export type ContextValueType = {
  config: AppFrameProps;
  /* Merges new props with default values */
  set: (newConfig: Partial<AppFrameProps>, key: any) => void;
  /* Merges the new props with previous props */
  delete: (key: any) => void;
};

const AppFrameContext = createContext<ContextValueType>({
  config: initialState,
  set: () => null,
  delete: () => null,
});

interface AppFrameLayoutProps {
  children?: React.ReactNode;
}

const AppFrameLayout: React.FC<AppFrameLayoutProps> = ({ children }) => {
  const [configStack, setConfigStack] = useState<Record<any, AppFrameProps>>({
    0: initialState,
  });
  const [configPriority, setConfigPriority] = useState([0]);
  const currentConfig = configStack[configPriority[0]];

  const [searchParams] = useSearchParams();
  const nochrome = searchParams.get('nochrome');
  const location = useLocation();
  const isAdminPortal = useIsAdminPortal();

  // Check if current path is the messaging page
  const isMessagingPage = location.pathname.includes('/messages');

  const addConfig = useCallback(
    (newConfig, key) => {
      setConfigStack((prevStack) => {
        const existingConfig = prevStack[key];
        if (existingConfig) {
          const updatedConfig = { ...existingConfig, ...newConfig };
          return {
            ...prevStack,
            [key]: updatedConfig,
          };
        }
        return {
          ...prevStack,
          [key]: { ...initialState, ...newConfig },
        };
      });
      setConfigPriority((oldPriority) => {
        if (oldPriority.includes(key)) {
          return oldPriority;
        }
        return [key, ...oldPriority];
      });
    },
    [setConfigStack, setConfigPriority]
  );

  const removeConfig = useCallback(
    (key) => {
      setConfigStack((prevStack) => {
        const newStack = prevStack;
        delete newStack[key];
        return newStack;
      });
      setConfigPriority((oldPriority) => oldPriority.filter((v) => v !== key));
    },
    [setConfigStack, setConfigPriority]
  );

  const contextValue: ContextValueType = useMemo(() => {
    return {
      config: currentConfig,
      set: addConfig,
      delete: removeConfig,
    };
  }, [addConfig, currentConfig, removeConfig]);

  return (
    <>
      <AppFrame
        title={currentConfig.title}
        collapsedTitle={currentConfig.collapsedTitle}
        drawerOpen={currentConfig.drawerOpen}
        frameOnly={currentConfig.frameOnly}
        actions={currentConfig.actions}
        row={currentConfig.row}
        compactTitle={currentConfig.compactTitle}
        containerMaxWidth={currentConfig.containerMaxWidth}
        backButtonTitle={currentConfig.backButtonTitle}
        backButtonLinkTo={currentConfig.backButtonLinkTo}
        bgcolor={currentConfig.bgcolor}
        noChrome={!!nochrome}
        disabled={currentConfig.disabled}
        fixedHeader={currentConfig.fixedHeader}
      >
        <WelcomeModal />
        <AppFrameContext.Provider value={contextValue}>
          <Suspense>{children || <Outlet />}</Suspense>
        </AppFrameContext.Provider>
      </AppFrame>
      {!isAdminPortal && !isMessagingPage && <OnboardingProgressPill />}
    </>
  );
};

export default AppFrameLayout;

export const useAppFrame = () => {
  const context = useContext(AppFrameContext);
  return context as ContextValueType;
};
